import { enTetesAuth } from './enTetesAuth';
/**
 * Service for processing OCR text with Google Gemini AI via backend proxy
 * All Gemini API calls go through the backend proxy at /gemini/*
 * The API key is kept server-side and never exposed to the client.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Helper: get current peopleId from localStorage
 * @returns {number}
 * @throws {Error} If user is not logged in
 */
const getPeopleId = () => {
    const peopleId = parseInt(localStorage.getItem('userId'), 10);
    if (!peopleId) throw new Error('User not logged in');
    return peopleId;
};

/**
 * Helper: handle proxy response uniformly
 * @param {Response} response
 * @returns {Promise<any>} The data field from the proxy response
 */
const GEMINI_TIMEOUT_MS = 30000;

const fetchWithTimeout = (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), GEMINI_TIMEOUT_MS);
    return fetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(timer));
};

const handleProxyResponse = async (response) => {
    if (response.status === 429) {
        const err = await response.json();
        throw new Error(`Limite Gemini atteinte (${err.hourCount}/h, ${err.dayCount}/j)`);
    }

    if (!response.ok) {
        throw new Error(`Gemini proxy error: ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) throw new Error(result.error || 'Gemini error');
    return result.data;
};

/**
 * Analyzes OCR text with Gemini to extract business card information
 * @param {string} ocrText - Raw text extracted from OCR
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<Object>} Extracted and structured data
 */
export const analyzeBusinessCardWithGemini = async (ocrText, language = 'fr') => {
    try {
        const peopleId = getPeopleId();

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/business-card`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, ocrText, language })
        });

        return await handleProxyResponse(response);

    } catch (error) {
        console.error('Error analyzing with Gemini:', error);
        throw error;
    }
};

/**
 * Cached Gemini availability — populated once at app boot via initGeminiAvailability().
 * `null` means "not yet checked" — `isGeminiAvailable()` returns false in that state (fail-safe).
 */
let _geminiAvailable = null;

/**
 * Initialize the Gemini availability cache. Call once at app boot.
 * Fetches /gemini/availability and stores the boolean result in `_geminiAvailable`.
 * On network/API error, sets `_geminiAvailable = false` so the UI hides Gemini-dependent buttons.
 * @returns {Promise<boolean>} The resolved availability (also cached for sync access).
 */
export const initGeminiAvailability = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/gemini/availability`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            _geminiAvailable = false;
            return false;
        }

        const result = await response.json();
        if (!result.success) {
            _geminiAvailable = false;
            return false;
        }

        const available = result.data === true || result.data?.available === true;
        _geminiAvailable = !!available;
        return _geminiAvailable;

    } catch (error) {
        console.error('Error checking Gemini availability:', error);
        _geminiAvailable = false;
        return false;
    }
};

/**
 * Synchronous getter for Gemini availability.
 * Returns false until `initGeminiAvailability()` resolves (fail-safe: hide Gemini UI by default).
 * @returns {boolean} True only if availability check completed and Gemini is available.
 */
export const isGeminiAvailable = () => {
    return _geminiAvailable === true;
};

/**
 * Enhances a product image using Gemini AI via backend proxy
 * Can improve quality, remove background, or place product in a new context
 * @param {string} imageUrl - URL of the image to enhance
 * @param {Object} options - Enhancement options
 * @param {string} options.mode - Enhancement mode: 'enhance', 'removeBackground', 'changeContext'
 * @param {string} options.customPrompt - Custom prompt for specific transformations
 * @param {string} options.context - New context/background description (for changeContext mode)
 * @param {string} options.language - Language code for prompts
 * @returns {Promise<Object>} { success, enhancedBase64, mimeType, error }
 */
export const enhanceImageWithAI = async (imageUrl, options = {}) => {
    const {
        mode = 'enhance',
        customPrompt = '',
        context = '',
        language = 'fr'
    } = options;

    try {
        const peopleId = getPeopleId();

        // Fetch the image and convert to base64
        console.log('Fetching image from:', imageUrl);
        const imageResponse = await fetch(imageUrl);
        if (!imageResponse.ok) {
            throw new Error(`Failed to fetch image: ${imageResponse.status}`);
        }

        const imageBlob = await imageResponse.blob();
        const mimeType = imageBlob.type || 'image/jpeg';

        // Convert blob to base64
        const base64Image = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
        });

        console.log('Sending image to Gemini proxy for enhancement, mode:', mode);

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/enhance-image`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({
                peopleId,
                imageBase64: base64Image,
                mimeType,
                mode,
                customPrompt,
                context,
                language
            })
        });

        if (response.status === 429) {
            const err = await response.json();
            return {
                success: false,
                error: `Limite Gemini atteinte (${err.hourCount}/h, ${err.dayCount}/j)`
            };
        }

        if (!response.ok) {
            console.error('Gemini proxy error:', response.status);
            return { success: false, error: `Gemini proxy error: ${response.status}` };
        }

        const result = await response.json();

        if (!result.success) {
            return { success: false, error: result.error || 'Gemini error' };
        }

        console.log('Enhanced image received successfully');
        return {
            success: true,
            enhancedBase64: result.enhancedBase64,
            mimeType: result.mimeType || 'image/png'
        };

    } catch (error) {
        console.error('Error enhancing image with AI:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Generates a place description using Gemini AI based on Google Place data
 * Backend may also browse the website to get more context about the business
 * @param {Object} placeData - Data from Google Places API
 * @param {string} language - Language code ('fr', 'en', 'nl', etc.)
 * @returns {Promise<string>} Generated description
 */
export const generatePlaceDescription = async (placeData, language = 'fr') => {
    try {
        console.log('Generating place description with Gemini (with web browsing)...');
        const peopleId = getPeopleId();

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/place-description`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, placeData, language })
        });

        const data = await handleProxyResponse(response);
        const generatedText = typeof data === 'string' ? data : (data?.description || data?.text || '');

        console.log('Generated description:', generatedText);
        return generatedText;

    } catch (error) {
        console.error('Error generating description with Gemini:', error);
        throw error;
    }
};

/**
 * Analyzes a website URL with Gemini AI via backend proxy
 * Generates title, description, keywords, category in ONE language + contact info
 * @param {string} url - Website URL to analyze
 * @param {string} language - Language code (fr, en, nl, de, es, it, pt, el)
 * @returns {Promise<Object>} Extracted website data for the specified language
 */
export const analyzeWebsite = async (url, language = 'fr') => {
    try {
        console.log(`Analyzing website with Gemini (${language}):`, url);
        const peopleId = getPeopleId();

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/analyze-website`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, url, language })
        });

        const data = await handleProxyResponse(response);

        // Ensure SocialLinks is a string for the API (preserve existing behavior)
        if (data.SocialLinks && typeof data.SocialLinks === 'object') {
            data.SocialLinks = JSON.stringify(data.SocialLinks);
        }

        console.log(`Website analysis completed (${language})`);
        return data;

    } catch (error) {
        console.error('Error analyzing website with Gemini:', error);
        throw error;
    }
};

/**
 * Enhances text content using Gemini AI via backend proxy
 * Improves clarity, grammar, and professional tone
 * @param {string} text - Text to enhance
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<string>} Enhanced text
 */
export const enhanceTextWithAI = async (text, language = 'fr') => {
    if (!text || text.trim().length < 10) {
        return text;
    }

    try {
        const peopleId = getPeopleId();

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/enhance-text`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, text, language })
        });

        if (response.status === 429) {
            const err = await response.json();
            console.error(`Limite Gemini atteinte (${err.hourCount}/h, ${err.dayCount}/j)`);
            return text;
        }

        if (!response.ok) {
            console.error('Gemini proxy error:', response.status);
            return text;
        }

        const result = await response.json();
        if (!result.success) {
            console.error('Gemini error:', result.error);
            return text;
        }

        const enhanced = typeof result.data === 'string' ? result.data : (result.data?.text || result.data?.enhancedText || '');
        return enhanced || text;

    } catch (error) {
        console.error('Error enhancing text with AI:', error);
        return text;
    }
};

/**
 * Translates website content from source language to all other languages via backend proxy
 * @param {Object} sourceContent - Content in source language { title, description, keywords, category }
 * @param {string} sourceLang - Source language code
 * @returns {Promise<Object>} Translations for all 7 other languages
 */
export const translateWebsiteContent = async (sourceContent, sourceLang = 'fr') => {
    try {
        console.log(`Translating website content from ${sourceLang} to 7 languages...`);
        const peopleId = getPeopleId();

        const response = await fetchWithTimeout(`${API_BASE_URL}/gemini/translate-website`, {
            method: 'POST',
            headers: enTetesAuth(),
            credentials: 'include',
            body: JSON.stringify({ peopleId, sourceContent, sourceLang })
        });

        const data = await handleProxyResponse(response);
        console.log('Translation completed for 7 languages');
        return data;

    } catch (error) {
        console.error('Error translating website content:', error);
        throw error;
    }
};

